// src/pages/Admin/Licencies/LicenciesAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";
import {
  getLicencies,
  addLicencie,
  updateLicencie,
  deleteLicencie,
} from "../../../services/licenciesServices";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaSortAlphaDown,
  FaSortAlphaUp,
  FaSyncAlt,
  FaIdCard,
  FaBuilding,
  FaLayerGroup,
  FaVenusMars,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

const SAISON_RE = /^\d{4}-\d{4}$/;

const guessDefaultSeasonLabel = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const getGenreBadgeClass = (genre) => {
  if (genre === "F") {
    return "border-pink-200 bg-pink-50 text-pink-800 dark:border-pink-500/20 dark:bg-pink-500/10 dark:text-pink-300";
  }
  if (genre === "M") {
    return "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300";
  }
  return "border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200";
};

const sortRowsByName = (rows, asc = true) => {
  return [...rows].sort((a, b) => {
    const an = normalize(`${a.nom} ${a.prenom}`);
    const bn = normalize(`${b.nom} ${b.prenom}`);
    return asc ? an.localeCompare(bn) : bn.localeCompare(an);
  });
};

/* =============== Small UI Parts =============== */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ total, clubs, saisons, femmes }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Gestion des licenciés
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Consulte, recherche, filtre, crée et modifie les licenciés de la
              base de données.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:min-w-[820px]">
            <CompactStat
              icon={<FaIdCard />}
              label="Licenciés"
              value={total}
              accent="indigo"
            />
            <CompactStat
              icon={<FaBuilding />}
              label="Clubs"
              value={clubs}
              accent="emerald"
            />
            <CompactStat
              icon={<FaLayerGroup />}
              label="Saisons"
              value={saisons}
              accent="amber"
            />
            <CompactStat
              icon={<FaVenusMars />}
              label="Licenciées"
              value={femmes}
              accent="rose"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  selectedSaison,
  setSelectedSaison,
  selectedGenre,
  setSelectedGenre,
  saisons,
  sortAsc,
  setSortAsc,
  onRefresh,
  onCreate,
  onReset,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),220px,180px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Nom, prénom, club, licence, saison…"
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={selectedSaison}
          onChange={(e) => setSelectedSaison(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="">Toutes les saisons</option>
          {saisons.map((saison) => (
            <option key={saison} value={saison}>
              {saison}
            </option>
          ))}
        </select>

        <select
          value={selectedGenre}
          onChange={(e) => setSelectedGenre(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="">Tous les genres</option>
          <option value="M">Masculin</option>
          <option value="F">Féminin</option>
        </select>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setSortAsc((s) => !s)}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            title="Inverser le tri A ↔ Z"
          >
            {sortAsc ? <FaSortAlphaDown /> : <FaSortAlphaUp />}
            {sortAsc ? "A → Z" : "Z → A"}
          </button>

          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt />
            Rafraîchir
          </button>

          <button
            type="button"
            onClick={onCreate}
            className="inline-flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-medium text-white transition hover:bg-emerald-700"
          >
            <FaPlus />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function GenreBadge({ genre }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${getGenreBadgeClass(
        genre,
      )}`}
    >
      {genre || "—"}
    </span>
  );
}

function DesktopTable({ rows, onEdit, onDelete }) {
  return (
    <div className="hidden xl:block rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-slate-500">
          Aucun licencié ne correspond aux filtres.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[1180px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[18%] px-4 py-3 font-semibold">Licence</th>
                <th className="w-[22%] px-4 py-3 font-semibold">Licencié</th>
                <th className="w-[28%] px-4 py-3 font-semibold">Club</th>
                <th className="w-[10%] px-4 py-3 font-semibold">Genre</th>
                <th className="w-[12%] px-4 py-3 font-semibold">Saison</th>
                <th className="w-[10%] px-4 py-3 font-semibold text-center">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-4">
                    <div className="font-mono text-[13px] font-semibold text-slate-800 dark:text-slate-200">
                      {row.licence_number}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {row.nom} {row.prenom}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div
                      className="line-clamp-3 leading-5 text-slate-600 dark:text-slate-300"
                      title={row.club}
                    >
                      {row.club}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <GenreBadge genre={row.genre} />
                  </td>

                  <td className="px-4 py-4">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {row.saison}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onEdit(row)}
                        className="inline-flex h-9 items-center gap-2 rounded-xl bg-indigo-600 px-3 text-xs font-medium text-white transition hover:bg-indigo-700"
                        title="Modifier"
                      >
                        <FaEdit />
                        {/* Éditer */}
                      </button>

                      <button
                        onClick={() => onDelete(row)}
                        className="inline-flex h-9 items-center gap-2 rounded-xl bg-red-600 px-3 text-xs font-medium text-white transition hover:bg-red-700"
                        title="Supprimer"
                      >
                        <FaTrash />
                        {/* Supprimer */}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MobileCards({ rows, onEdit, onDelete }) {
  return (
    <div className="xl:hidden space-y-3">
      {rows.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Aucun licencié ne correspond aux filtres.
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={row.id}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                  {row.nom} {row.prenom}
                </div>
                <div className="mt-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                  {row.licence_number}
                </div>
              </div>

              <GenreBadge genre={row.genre} />
            </div>

            <div className="mt-3 grid gap-2 text-sm text-slate-600 dark:text-slate-300">
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Club :
                </span>{" "}
                {row.club}
              </div>
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Saison :
                </span>{" "}
                {row.saison}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => onEdit(row)}
                className="inline-flex h-10 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-medium text-white transition hover:bg-indigo-700"
              >
                <FaEdit />
                Éditer
              </button>

              <button
                onClick={() => onDelete(row)}
                className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-medium text-white transition hover:bg-red-700"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {page}
        </span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* =============== Form Modal (Create / Edit) =============== */
function LicencieFormModal({ isOpen, onClose, onSubmit, initialData = null }) {
  const isEdit = Boolean(initialData?.id);
  const { addNotification } = useNotification();

  const [nom, setNom] = useState(initialData?.nom || "");
  const [prenom, setPrenom] = useState(initialData?.prenom || "");
  const [club, setClub] = useState(initialData?.club || "");
  const [genre, setGenre] = useState(initialData?.genre || "M");
  const [licence, setLicence] = useState(initialData?.licence_number || "");
  const [saison, setSaison] = useState(
    initialData?.saison || guessDefaultSeasonLabel(),
  );
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setNom(initialData?.nom || "");
    setPrenom(initialData?.prenom || "");
    setClub(initialData?.club || "");
    setGenre(initialData?.genre || "M");
    setLicence(initialData?.licence_number || "");
    setSaison(initialData?.saison || guessDefaultSeasonLabel());
    setSending(false);
  }, [isOpen, initialData]);

  const validate = () => {
    if (!nom || !prenom || !club || !genre || !licence || !saison) {
      addNotification("Tous les champs sont obligatoires.", "error");
      return false;
    }
    if (!["M", "F"].includes(genre)) {
      addNotification("Le genre doit être 'M' ou 'F'.", "error");
      return false;
    }
    if (!SAISON_RE.test(saison)) {
      addNotification("La saison doit être au format 'YYYY-YYYY'.", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    try {
      setSending(true);
      await onSubmit({
        nom: nom.trim(),
        prenom: prenom.trim(),
        club: club.trim(),
        genre,
        licence_number: licence.trim().toUpperCase(),
        saison: saison.trim(),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="licencie-form-title"
      describedById="licencie-form-desc"
      initialFocusSelector='input[name="licencie-nom"]'
    >
      <ModalHeader
        id="licencie-form-title"
        title={isEdit ? "Modifier un licencié" : "Créer un licencié"}
        subtitle="Renseignez les informations du licencié"
        onClose={onClose}
      />

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody id="licencie-form-desc">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Nom
              </label>
              <input
                data-autofocus
                name="licencie-nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Prénom
              </label>
              <input
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Club
              </label>
              <input
                value={club}
                onChange={(e) => setClub(e.target.value)}
                placeholder="Nom du club"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Genre
              </label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              >
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                N° de licence
              </label>
              <input
                value={licence}
                onChange={(e) => setLicence(e.target.value.toUpperCase())}
                placeholder="Ex : M10071971VICTO01"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 font-mono text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Saison
              </label>
              <input
                value={saison}
                onChange={(e) => setSaison(e.target.value)}
                placeholder="YYYY-YYYY"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 font-mono text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                required
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Format attendu : <span className="font-mono">2025-2026</span>
              </p>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            text="Annuler"
            type="button"
            onClick={onClose}
            className="!rounded-2xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
          />
          <Button
            text={
              sending
                ? "Enregistrement..."
                : isEdit
                  ? "Enregistrer"
                  : "Créer"
            }
            type="submit"
            loading={sending}
            disabled={sending}
            className="!rounded-2xl"
          />
        </ModalFooter>
      </form>
    </Modal>
  );
}

/* ===================== Page ===================== */
export default function LicenciesAdmin() {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 300);
  const [selectedSaison, setSelectedSaison] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLicencies();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      addNotification("Erreur lors du chargement des licenciés.", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saisons = useMemo(() => {
    const values = [...new Set(rows.map((r) => r.saison).filter(Boolean))];
    return values.sort((a, b) => (a > b ? -1 : 1));
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows.slice();

    if (selectedSaison) {
      list = list.filter((r) => r.saison === selectedSaison);
    }

    if (selectedGenre) {
      list = list.filter((r) => r.genre === selectedGenre);
    }

    const q = normalize(debouncedSearch);
    if (q) {
      list = list.filter((r) =>
        normalize(
          `${r.nom} ${r.prenom} ${r.club} ${r.licence_number} ${r.saison}`,
        ).includes(q),
      );
    }

    return sortRowsByName(list, sortAsc);
  }, [rows, debouncedSearch, selectedSaison, selectedGenre, sortAsc]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedSaison, selectedGenre, sortAsc]);

  const stats = useMemo(() => {
    const total = rows.length;
    const clubs = new Set(rows.map((r) => normalize(r.club)).filter(Boolean)).size;
    const saisonsCount = new Set(rows.map((r) => r.saison).filter(Boolean)).size;
    const femmes = rows.filter((r) => r.genre === "F").length;

    return { total, clubs, saisons: saisonsCount, femmes };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  /* ============ Handlers CRUD ============ */
  const openCreate = () => {
    setEditRow(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setFormOpen(true);
  };

  const handleSubmitForm = async (payload) => {
    if (editRow?.id) {
      const updated = await updateLicencie(editRow.id, payload);

      if (!updated) {
        addNotification("Échec de la mise à jour.", "error");
        return;
      }

      addNotification("Licencié mis à jour.", "success");
      setRows((prev) =>
        prev.map((r) => (r.id === editRow.id ? { ...r, ...payload } : r)),
      );
    } else {
      const created = await addLicencie(payload);

      if (!created) {
        addNotification("Échec de la création.", "error");
        return;
      }

      addNotification("Licencié créé avec succès.", "success");
      setRows((prev) => [created, ...prev]);
    }

    setFormOpen(false);
    setEditRow(null);
  };

  const onDelete = async (row) => {
    if (
      !window.confirm(
        `Supprimer ${row.prenom} ${row.nom} (${row.licence_number}) ?`,
      )
    ) {
      return;
    }

    const ok = await deleteLicencie(row.id);

    if (!ok) {
      addNotification("Erreur lors de la suppression.", "error");
      return;
    }

    addNotification("Licencié supprimé.", "success");
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedSaison("");
    setSelectedGenre("");
    setSortAsc(true);
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          total={stats.total}
          clubs={stats.clubs}
          saisons={stats.saisons}
          femmes={stats.femmes}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedSaison={selectedSaison}
            setSelectedSaison={setSelectedSaison}
            selectedGenre={selectedGenre}
            setSelectedGenre={setSelectedGenre}
            saisons={saisons}
            sortAsc={sortAsc}
            setSortAsc={setSortAsc}
            onRefresh={fetchAll}
            onCreate={openCreate}
            onReset={handleReset}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filtered.length} licencié{filtered.length > 1 ? "s" : ""}
          </span>

          {selectedSaison && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Saison : {selectedSaison}
            </span>
          )}

          {selectedGenre && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Genre : {selectedGenre}
            </span>
          )}

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}

          {filtered.length > 0 && (
            <span className="ml-auto text-xs">
              Affichage {pageItems.length} • page {safePage} / {totalPages}
            </span>
          )}
        </div>

        <div className="mt-5">
          <DesktopTable rows={pageItems} onEdit={openEdit} onDelete={onDelete} />

          <MobileCards rows={pageItems} onEdit={openEdit} onDelete={onDelete} />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={totalPages}
          setPage={setPage}
        />
      </div>

      <LicencieFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditRow(null);
        }}
        onSubmit={handleSubmitForm}
        initialData={editRow}
      />
    </div>
  );
}